import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Spinner, Alert, Button } from 'react-bootstrap';
import { FiUsers, FiMapPin, FiHome, FiChevronRight, FiGlobe } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import AppFooter from '@core/components/AppFooter';
import Header from '@core/components/Header';
import { UserAccount, EntityScope } from '../types/entity';
import { fetchUserAccounts } from '../api/entityApi';
import { getApiScopeFromValue } from '@features/invitations/utils/getScopeFromParam';
import { showInfo } from '@infrastructure/api/utils/toastNotifications';
import PendingInvitationsBanner from './PendingInvitationsBanner';
import { entityNameCacheKey } from '@features/auth/constants/sessionStorageKeys';
import '@features/dashboard/pages/dashboard.css';
import './EntitySelector.css';

type ScopeFilter = 'ALL' | EntityScope;

const EntitySelector: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [accounts, setAccounts] = useState<UserAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('ALL');
  const accessDeniedToastShownRef = useRef(false);

  useEffect(() => {
    loadUserAccounts();
  }, []);

  useEffect(() => {
    const state = location.state as { accessDenied?: boolean } | null;
    if (!state?.accessDenied || accessDeniedToastShownRef.current) return;

    accessDeniedToastShownRef.current = true;
    navigate('/entities', { replace: true, state: {} });
    showInfo("You don't have access to that workspace. Select one of your accounts below.");
  }, [location.state, navigate]);

  const loadUserAccounts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchUserAccounts();

      if (response && response.isSuccess() && response.data) {
        setAccounts(response.data.data);
      } else if (response) {
        const errorData = response.getError ? response.getError() : response.error;
        console.error('Failed to load accounts:', errorData);

        if (response.status === 500) {
          setError('Server error: Unable to load accounts. Please try again later or contact support.');
        } else if (response.status === 404) {
          setError('No accounts found. Please contact your administrator.');
        } else {
          setError(errorData?.detail || 'Failed to load accounts');
        }
      }
    } catch (err: any) {
      console.error('Error loading accounts:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getScopeIcon = (scope: EntityScope) => {
    switch (scope) {
      case 'NATION':
        return <FiUsers className="display-6" />;
      case 'DISTRICT':
        return <FiMapPin className="display-6" />;
      case 'CHURCH':
        return <FiHome className="display-6" />;
      default:
        return <FiUsers className="display-6" />;
    }
  };

  const getScopeColor = (scope: EntityScope) => {
    switch (scope) {
      case 'NATION':
        return 'danger';
      case 'DISTRICT':
        return 'info';
      case 'CHURCH':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const handleEntitySelect = (account: UserAccount) => {
    const cacheKey = entityNameCacheKey(account.scope, account.scope_ref_id);
    sessionStorage.setItem(cacheKey, account.scope_name);

    const dashboardPath = `/${getApiScopeFromValue(account.scope)}/${account.scope_ref_id}/dashboard`;
    navigate(dashboardPath);
  };

  const availableScopes = useMemo(() => {
    const scopes = new Set(accounts.map(a => a.scope));
    return ['NATION', 'DISTRICT', 'CHURCH'].filter(s => scopes.has(s as EntityScope)) as EntityScope[];
  }, [accounts]);

  const filteredAccounts = useMemo(() => {
    if (scopeFilter === 'ALL') return accounts;
    return accounts.filter(a => a.scope === scopeFilter);
  }, [accounts, scopeFilter]);

  const getScopeFilterIcon = (scope: ScopeFilter) => {
    switch (scope) {
      case 'NATION': return <FiGlobe className="me-1" />;
      case 'DISTRICT': return <FiMapPin className="me-1" />;
      case 'CHURCH': return <FiHome className="me-1" />;
      default: return null;
    }
  };

  const getScopeCount = (scope: ScopeFilter) => {
    if (scope === 'ALL') return accounts.length;
    return accounts.filter(a => a.scope === scope).length;
  };

  const entitiesLayout = (content: React.ReactNode) => (
    <div className="ntcog-app-shell">
      <Header title="" subtitle="" />
      <div className="ntcog-app-shell__content d-flex flex-column">
        <main
          id="main"
          className="flex-grow-1 d-flex flex-column"
          tabIndex={-1}
        >
          {content}
        </main>
        <AppFooter />
      </div>
    </div>
  );

  const renderEmptyHome = () => (
    <div className="entity-selector">
      <div className="ntcog-page-content d-flex flex-column flex-grow-1">
        <div className="mb-3">
          <PendingInvitationsBanner />
        </div>
        <div className="flex-grow-1 d-flex align-items-center justify-content-center py-5">
          <div className="entity-selector__empty-card card border-0 p-4 p-md-5 text-center">
            <div className="card-body p-0">
              <h1 className="entity-selector__empty-title">Welcome</h1>
              <p className="entity-selector__empty-text mb-0">
                You don&apos;t have access to any workspace yet. Accept an invitation or finish signing up to get started.
              </p>
              <div className="entity-selector__empty-actions">
                <Button variant="primary" className="px-4" onClick={() => navigate('/pending-invitations')}>
                  View invitations
                </Button>
                <Button variant="outline-primary" onClick={() => navigate('/register')}>
                  Finish signing up
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return entitiesLayout(
      <div className="entity-selector">
        <div className="ntcog-page-content d-flex flex-column flex-grow-1">
          <div className="entity-selector__loading">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="entity-selector__loading-text mb-0">Loading your accounts…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return entitiesLayout(
      <div className="entity-selector">
        <div className="ntcog-page-content pt-4">
          <Alert variant="danger" className="mb-0">
            <Alert.Heading>Something went wrong</Alert.Heading>
            <p className="mb-3">{error}</p>
            <Button variant="outline-danger" size="sm" onClick={loadUserAccounts}>
              Try again
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  if (accounts.length === 0) {
    return entitiesLayout(renderEmptyHome());
  }

  const getIconWrapClass = (scope: EntityScope) => {
    switch (scope) {
      case 'NATION': return 'entity-card-select__icon-wrap--nation';
      case 'DISTRICT': return 'entity-card-select__icon-wrap--district';
      case 'CHURCH': return 'entity-card-select__icon-wrap--church';
      default: return 'entity-card-select__icon-wrap--nation';
    }
  };

  const getScopeLabel = (scope: EntityScope) => {
    switch (scope) {
      case 'NATION': return 'Nation';
      case 'DISTRICT': return 'District';
      case 'CHURCH': return 'Church';
      default: return scope;
    }
  };

  return entitiesLayout(
    <div className="entity-selector d-flex flex-column flex-grow-1">
      <div className="ntcog-page-content d-flex flex-column flex-grow-1">
        <div className="mb-3">
          <PendingInvitationsBanner />
        </div>

        <header className="entity-selector__hero">
          <h1 className="entity-selector__hero-title">Your workspaces</h1>
          <p className="entity-selector__hero-sub">Select an account to open its dashboard.</p>
        </header>

        {availableScopes.length > 1 && (
          <div className="entity-selector__filters" role="group" aria-label="Filter by type">
            <Button
              variant={scopeFilter === 'ALL' ? 'primary' : 'outline-secondary'}
              size="sm"
              className="entity-selector__filter-btn"
              onClick={() => setScopeFilter('ALL')}
            >
              All ({getScopeCount('ALL')})
            </Button>
            {availableScopes.map((scope) => (
              <Button
                key={scope}
                variant={scopeFilter === scope ? getScopeColor(scope) : 'outline-secondary'}
                size="sm"
                className="entity-selector__filter-btn"
                onClick={() => setScopeFilter(scope)}
              >
                {getScopeFilterIcon(scope)}
                {scope.charAt(0) + scope.slice(1).toLowerCase()} ({getScopeCount(scope)})
              </Button>
            ))}
          </div>
        )}

        <div className="entity-selector__list" role="list">
          {filteredAccounts.map((account) => {
            const dashboardPath = `/${getApiScopeFromValue(account.scope)}/${account.scope_ref_id}/dashboard`;
            return (
              <div key={`${account.scope}-${account.scope_ref_id}`} role="listitem">
                <a
                  href={dashboardPath}
                  className="entity-card-select"
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    handleEntitySelect(account);
                  }}
                >
                  <span className={`entity-card-select__icon-wrap ${getIconWrapClass(account.scope)}`} aria-hidden>
                    {getScopeIcon(account.scope)}
                  </span>
                  <div className="entity-card-select__body">
                    <div className="entity-card-select__label">{getScopeLabel(account.scope)}</div>
                    <p className="entity-card-select__name">{account.scope_name}</p>
                  </div>
                  <FiChevronRight className="entity-card-select__arrow" size={20} aria-hidden />
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EntitySelector;
